import {
  PLATFORM_SERVICES_COLLECTION,
  USER_DATA_AND_CHATBOT_COLLECTION,
} from "constants/global/firebase-collections";
import { deleteField, doc, onSnapshot, updateDoc } from "firebase/firestore";
import {
  FB_ADS_ACCOUNTS,
  IS_EXTERNAL_API,
  IS_REBILLING,
  PLATFROM_SERVICES,
  USER_DATA,
  WORKSPACE_ID,
  WORKSPACE_USER_DATA,
} from "types/app";

let platformServicesUnsubscriber: (() => void) | null = null;
const workspaceUserDataUnsubscribers: Record<string, () => void> = {};
let userDataUnsubscriber: (() => void) | null = null;
let activePlatformServiceId: string | null = null;

const stopPlatformServicesListener = () => {
  platformServicesUnsubscriber?.();
  platformServicesUnsubscriber = null;
};

/**
 * Listen to a single platformServices doc and replace Redux state with it.
 * Drops any previous listener so connector/user ids cannot merge with workspace data.
 */
const setActivePlatformServiceId = (id: string | null, dispatch?: any) => {
  if (activePlatformServiceId === id) {
    return;
  }

  stopPlatformServicesListener();
  activePlatformServiceId = id;

  if (!id) {
    dispatch?.(setPlatformServices({}));
    return;
  }

  platformServicesUnsubscriber = onSnapshot(
    doc(PLATFORM_SERVICES_COLLECTION, id),
    (response) => {
      if (activePlatformServiceId !== id) {
        return;
      }
      dispatch?.(setPlatformServices(response.data() || {}));
    },
  );
};

const ensurePlatformServicesListener = (id: string, dispatch: any) => {
  if (!id) {
    return;
  }

  if (activePlatformServiceId === id && platformServicesUnsubscriber) {
    return;
  }

  setActivePlatformServiceId(id, dispatch);
};

export const getActivePlatformServiceIds = (): string[] =>
  activePlatformServiceId ? [activePlatformServiceId] : [];

/**
 * Remove a platform field from the active workspace platformServices doc.
 */
export const clearPlatformServiceField = (
  platformField: string,
  fallbackIds: string[] = [],
): any => {
  return async (dispatch: any, getState: () => any) => {
    const id =
      activePlatformServiceId ||
      fallbackIds.find(Boolean) ||
      null;

    if (!id) {
      return;
    }

    const { platformServices = {} } = getState().appReducer || {};
    const next = { ...platformServices };
    delete next[platformField];
    dispatch(setPlatformServices(next));

    try {
      await updateDoc(doc(PLATFORM_SERVICES_COLLECTION, id), {
        [platformField]: deleteField(),
      });
    } catch (error) {
      console.log(error, `clearPlatformServiceField:${id}`);
    }
  };
};

export const getUserData = (id: any, isExternalApi: boolean = false): any => {
  return (dispatch: any) => {
    userDataUnsubscriber?.();

    userDataUnsubscriber = onSnapshot(
      doc(USER_DATA_AND_CHATBOT_COLLECTION, id),
      (response) => {
        const data = response.data() || {};
        const workspaceId = isExternalApi
          ? id
          : data?.lastSelectedWorkspace?.workspaceId || id;

        dispatch(setWorkspaceId(workspaceId));
        ensurePlatformServicesListener(workspaceId, dispatch);
        dispatch(getWorkspaceUserData(workspaceId));

        if (!data.lastSelectedWorkspace || isExternalApi) {
          data.lastSelectedWorkspace = {
            workspaceId: id,
          };
        }
        dispatch(setUserData(data));
      },
    );
  };
};

export const getWorkspaceUserData = (id: any): any => {
  return (dispatch: any) => {
    if (!id) {
      return;
    }

    Object.keys(workspaceUserDataUnsubscribers).forEach((existingId) => {
      if (existingId !== id) {
        workspaceUserDataUnsubscribers[existingId]?.();
        delete workspaceUserDataUnsubscribers[existingId];
      }
    });

    if (workspaceUserDataUnsubscribers[id]) {
      return;
    }

    workspaceUserDataUnsubscribers[id] = onSnapshot(
      doc(USER_DATA_AND_CHATBOT_COLLECTION, id),
      (response) => {
        dispatch(setWorkspaceUserData(response.data() || {}));
      },
    );
  };
};

export const getPlatfromServices = (id: any): any => {
  return (dispatch: any) => {
    if (!id) {
      return;
    }

    ensurePlatformServicesListener(id, dispatch);
  };
};

export const setIsRebilling = (isRebilling: any) => {
  return {
    isRebilling,
    type: IS_REBILLING,
  };
};

export const setUserData = (userData: any) => {
  return {
    userData,
    type: USER_DATA,
  };
};

export const setWorkspaceUserData = (workspaceUserData: any) => {
  return {
    workspaceUserData,
    type: WORKSPACE_USER_DATA,
  };
};

export const setPlatformServices = (platformServices: any) => {
  return {
    platformServices,
    type: PLATFROM_SERVICES,
  };
};

export const setWorkspaceId = (workspaceId: any) => {
  return {
    workspaceId,
    type: WORKSPACE_ID,
  };
};

export const setFbAdsAccounts = (fbAdsAccounts: any) => {
  return {
    type: FB_ADS_ACCOUNTS,
    fbAdsAccounts,
  };
};

export const setIsExternalApi = (isExternalApi: boolean) => {
  return {
    type: IS_EXTERNAL_API,
    isExternalApi,
  };
};
